import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  FormGroup,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import ScheduleInput from '../ScheduleInput'; // Import the new component
import { deepMerge } from '../../utils/objectUtils'; // Import deepMerge

// Application services and Facility services use different key families
// (application: 'jumpStartLight'; facility: 'roadsideJumpStartsLightDuty').
// Translate when pre-filling the first facility from the application.
const mapAppServicesToFacility = (appServices = {}) => ({
  roadsideJumpStartsLightDuty: !!appServices.jumpStartLight,
  roadsideJumpStartsMediumDuty: !!appServices.jumpStartMedium,
  roadsideJumpStartsHeavyDuty: !!appServices.jumpStartHeavy,
  roadsideTireChangesLightDuty: !!appServices.tireChangeLight,
  roadsideTireChangesMediumDuty: !!appServices.tireChangeMedium,
  roadsideTireChangesHeavyDuty: !!appServices.tireChangeHeavy,
  roadsideLockOutsLightDuty: !!appServices.lockOutLight,
  roadsideLockOutsMediumDuty: !!appServices.lockOutMedium,
  roadsideLockOutsHeavyDuty: !!appServices.lockOutHeavy,
  roadsideFuelDeliveriesLightDuty: !!appServices.fuelDeliveryLight,
  roadsideFuelDeliveriesMediumDuty: !!appServices.fuelDeliveryMedium,
  roadsideFuelDeliveriesHeavyDuty: !!appServices.fuelDeliveryHeavy,
  winchOutsLightDuty: !!appServices.winchOutLight,
  winchOutsMediumDuty: !!appServices.winchOutMedium,
  winchOutsHeavyDuty: !!appServices.winchOutHeavy,
  lightDutyTowing: !!appServices.lightDutyTowing,
  mediumDutyTowing: !!appServices.mediumDutyTowing,
  heavyDutyTowing: !!appServices.heavyDutyTowing,
  accidentSceneTowing: !!appServices.accidentSceneTowing,
  secondaryTow: !!appServices.secondaryTow,
  mobileMechanic: !!appServices.mobileMechanic,
  mediumHeavyTire: !!appServices.mediumHeavyTire,
  storageFacility: !!appServices.storageFacility,
  keyMaking: !!appServices.keyMaking,
  locksmith: !!appServices.locksmith,
  electricCharging: !!appServices.electricCharging,
});

function FacilitiesTab({ application, editingFields, onUpdate, isReadOnly }) { // Add isReadOnly prop; editingFields carries unsaved Application-tab edits
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [facilityToDelete, setFacilityToDelete] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    facilityName: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    coveredZipCodes: [],
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    // operationalHours: '' // Removed
    services: {}, // Add services to initial form state
    schedule: {}, // Add schedule to initial form state
    open247: false // Add open247 state
  });

  // Get auth headers
  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { 
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };
  
  // Fetch facilities from the API
  const fetchFacilities = useCallback(async () => {
    if (!application || !application._id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/applications/${application._id}/facilities`, {
        headers: getAuthHeader()
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch facilities: ${response.statusText}`);
      }
      
      const data = await response.json();
      if (data && data.length === 0) {
        // No facilities exist: pre-populate the form from the application.
        // Use the LATEST application data — re-fetch the document (the list
        // prop can lag behind recent saves) and overlay any unsaved edits from
        // the Application tab, so the first facility always matches what the
        // reviewer currently sees there.
        let appData = application;
        try {
          const appResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/applications/${application._id}`, {
            headers: getAuthHeader()
          });
          if (appResponse.ok) {
            appData = await appResponse.json();
          }
        } catch (refreshError) {
          console.warn('Could not refresh application for facility pre-fill, using cached data:', refreshError);
        }
        const pendingEdits = editingFields?.[application._id];
        if (pendingEdits && Object.keys(pendingEdits).length > 0) {
          appData = deepMerge(appData, pendingEdits);
        }

        setFormData({
          facilityName: appData.companyName || '', // Use company name as default facility name
          address1: appData.facilityAddress1 || '',
          address2: appData.facilityAddress2 || '',
          city: appData.facilityCity || '',
          state: appData.facilityState || '',
          zip: appData.facilityZip || '',
          coveredZipCodes: appData.territories?.zipCodes || [], // Use main territory zips
      contactName: `${appData.ownerFirstName || ''} ${appData.ownerLastName || ''}`.trim(), // Default contact
      contactPhone: appData.phoneNumber || '',
      contactEmail: appData.email || '',
      // operationalHours: appData.services?.hoursOfOperation || '' // Removed
      services: mapAppServicesToFacility(appData.services), // Translate application service keys to facility service keys
      schedule: appData.services?.schedule || {}, // Pre-populate schedule from main app services if no facilities
      open247: appData.services?.open247 || false // Pre-populate open247
    });
    setIsAdding(true); // Set to adding mode to show the form ready for saving
    setSelectedFacility(null); // Ensure no facility is marked as selected
        setFacilities([]); // Ensure the list is empty
      } else {
        // Facilities exist, load them normally
        setFacilities(data);
        // Optionally select the first facility by default if needed
        // if (data && data.length > 0) {
        //   handleSelectFacility(data[0]);
        // }
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch facilities');
      console.error('Error fetching facilities:', err);
    } finally {
      setLoading(false);
    }
  }, [application, editingFields]);
  
  // Fetch facilities when component mounts or application changes
  useEffect(() => {
    fetchFacilities();
  }, [fetchFacilities]);
  
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'coveredZipCodes') {
      // Convert comma-separated string to array
      setFormData({
        ...formData,
        [name]: value.split(',').map(zip => zip.trim()).filter(zip => zip !== '')
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };
  
  // Handle selecting a facility for viewing/editing
  const handleSelectFacility = (facility) => {
    setSelectedFacility(facility);
    setFormData({
      facilityName: facility.facilityName || '',
      address1: facility.address1 || '',
      address2: facility.address2 || '',
      city: facility.city || '',
      state: facility.state || '',
      zip: facility.zip || '',
      coveredZipCodes: facility.coveredZipCodes || [],
      contactName: facility.contactName || '',
      contactPhone: facility.contactPhone || '',
      contactEmail: facility.contactEmail || '',
      // operationalHours: facility.operationalHours || '' // Removed
      services: facility.services || {}, // Load facility's services
      schedule: facility.schedule || {}, // Load facility's schedule
      open247: facility.open247 || false // Load open247 setting
    });
    setIsEditing(false);
    setIsAdding(false);
  };
  
  // Handle adding a new facility
  const handleAddFacility = () => {
    setSelectedFacility(null);
    setFormData({
      facilityName: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      zip: '',
      coveredZipCodes: [],
      contactName: '',
      contactPhone: '',
      contactEmail: '',
      // operationalHours: '' // Removed
      services: {}, // Start with empty services for new facility
      schedule: {}, // Start with empty schedule for new facility
      open247: false // Reset open247 for new facility
    });
    setIsEditing(false);
    setIsAdding(true);
  };
  
  // Handle editing a facility
  const handleEditFacility = () => {
    setIsEditing(true);
  };
  
  // Handle canceling edit/add
  const handleCancelEdit = () => {
    if (isAdding) {
      setIsAdding(false);
      setSelectedFacility(null);
    } else if (isEditing && selectedFacility) {
      setIsEditing(false);
      // Reset form data to selected facility
      setFormData({
        facilityName: selectedFacility.facilityName || '',
        address1: selectedFacility.address1 || '',
        address2: selectedFacility.address2 || '',
        city: selectedFacility.city || '',
        state: selectedFacility.state || '',
        zip: selectedFacility.zip || '',
        coveredZipCodes: selectedFacility.coveredZipCodes || [],
        contactName: selectedFacility.contactName || '',
        contactPhone: selectedFacility.contactPhone || '',
        contactEmail: selectedFacility.contactEmail || '',
        // operationalHours: selectedFacility.operationalHours || '' // Removed
        services: selectedFacility.services || {}, // Reset to selected facility's services
        schedule: selectedFacility.schedule || {}, // Reset to selected facility's schedule
        open247: selectedFacility.open247 || false // Reset open247
      });
  }
};
  
  // Handle saving a facility (add or edit)
  const handleSaveFacility = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      let response;
      
      if (isAdding) {
        // Add new facility
        response = await fetch(`${process.env.REACT_APP_API_URL}/api/applications/${application._id}/facilities`, {
          method: 'POST',
          headers: getAuthHeader(),
          body: JSON.stringify(formData)
        });
      } else if (isEditing && selectedFacility) {
        // Update existing facility
        response = await fetch(`${process.env.REACT_APP_API_URL}/api/facilities/${selectedFacility._id}`, {
          method: 'PUT',
          headers: getAuthHeader(),
          body: JSON.stringify(formData)
        });
      } else {
        throw new Error('Invalid operation');
      }
      
      if (!response.ok) {
        throw new Error(`Failed to ${isAdding ? 'add' : 'update'} facility: ${response.statusText}`);
      }
      
      const updatedFacility = await response.json();
      
      if (isAdding) {
        setFacilities([...facilities, updatedFacility]);
        setSuccess('Facility added successfully');
        setIsAdding(false);
        setSelectedFacility(updatedFacility);
      } else {
        setFacilities(facilities.map(f => 
          f._id === updatedFacility._id ? updatedFacility : f
        ));
        setSuccess('Facility updated successfully');
        setIsEditing(false);
        setSelectedFacility(updatedFacility);
      }
      
      // Call the onUpdate callback to refresh the parent component
      if (onUpdate) {
        onUpdate();
      }
    } catch (err) {
      setError(err.message || `Failed to ${isAdding ? 'add' : 'update'} facility`);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle opening delete confirmation dialog
  const handleDeleteClick = (facility) => {
    setFacilityToDelete(facility);
    setDeleteDialogOpen(true);
  };
  
  // Handle closing delete confirmation dialog
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setFacilityToDelete(null);
  };
  
  // Handle confirming facility deletion
  const handleConfirmDelete = async () => {
    if (!facilityToDelete) return;
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Correct the URL to include the application ID
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/applications/${application._id}/facilities/${facilityToDelete._id}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      
      if (!response.ok) {
        throw new Error(`Failed to delete facility: ${response.statusText}`);
      }
      
      // Remove the deleted facility from the list
      setFacilities(facilities.filter(f => f._id !== facilityToDelete._id));
      
      // If the deleted facility was selected, clear the selection
      if (selectedFacility && selectedFacility._id === facilityToDelete._id) {
        setSelectedFacility(null);
        setIsEditing(false);
      }
      
      setSuccess('Facility deleted successfully');
      
      // Call the onUpdate callback to refresh the parent component
      if (onUpdate) {
        onUpdate();
      }
    } catch (err) {
      setError(err.message || 'Failed to delete facility');
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setFacilityToDelete(null);
    }
  };
  
  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={3}>
        {/* Left Panel - Facility List */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '100%', minHeight: 400, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Facilities
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddFacility}
                disabled={loading || isReadOnly}
              >
                Add Facility
              </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />
            
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}
            
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                <CircularProgress />
              </Box>
            )}
            
            {facilities.length > 0 ? (
              <List sx={{ flexGrow: 1, overflow: 'auto' }}>
                {facilities.map((facility) => (
                  <ListItem
                    key={facility._id}
                    // removed button prop to fix React warning
                    selected={selectedFacility && selectedFacility._id === facility._id}
                    onClick={() => handleSelectFacility(facility)}
                    sx={{ cursor: 'pointer' }} // Add cursor pointer for better UX since 'button' is removed
                  >
                    <ListItemText
                      primary={facility.facilityName}
                      secondary={`${facility.city}, ${facility.state}`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => handleDeleteClick(facility)}
                        disabled={isReadOnly}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="textSecondary">
                  No facilities found. Click "Add Facility" to create one.
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
        
        {/* Right Panel - Facility Form */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                {isAdding ? 'Add New Facility' : 
                 isEditing ? 'Edit Facility' : 
                 selectedFacility ? 'Facility Details' : 'Select or Add a Facility'}
              </Typography>
              
              {selectedFacility && !isEditing && !isAdding && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<EditIcon />}
                  onClick={handleEditFacility}
                  disabled={isReadOnly}
                >
                  Edit
                </Button>
              )}
              
              {(isEditing || isAdding) && (
                <Box>
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<CancelIcon />}
                    onClick={handleCancelEdit}
                    sx={{ mr: 1 }}
                    disabled={isReadOnly} // Also disable cancel if read-only
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveFacility}
                    disabled={loading || isReadOnly}
                  >
                    Save
                  </Button>
                </Box>
              )}
            </Box>
            <Divider sx={{ mb: 2 }} />
            
            {(selectedFacility || isAdding) ? (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Facility Name"
                    name="facilityName"
                    value={formData.facilityName}
                    onChange={handleInputChange}
                    disabled={isReadOnly || (!isEditing && !isAdding)}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address Line 1"
                    name="address1"
                    value={formData.address1}
                    onChange={handleInputChange}
                    disabled={isReadOnly || (!isEditing && !isAdding)}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address Line 2"
                    name="address2"
                    value={formData.address2}
                    onChange={handleInputChange}
                    disabled={isReadOnly || (!isEditing && !isAdding)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="City"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    disabled={isReadOnly || (!isEditing && !isAdding)}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="State"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    disabled={isReadOnly || (!isEditing && !isAdding)}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="ZIP Code"
                    name="zip"
                    value={formData.zip}
                    onChange={handleInputChange}
                    disabled={isReadOnly || (!isEditing && !isAdding)}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <TextField
                      fullWidth
                      label="Covered ZIP Codes (comma-separated)"
                      name="coveredZipCodes"
                      value={formData.coveredZipCodes.join(', ')}
                      onChange={handleInputChange}
                      disabled={isReadOnly || (!isEditing && !isAdding)}
                      helperText="Enter ZIP codes separated by commas"
                    />
                    {(isEditing || isAdding) && (
                      <Button
                        variant="contained"
                        component="label"
                        size="small"
                        sx={{ mt: 1, minWidth: '140px' }}
                        disabled={isReadOnly}
                      >
                        Upload Zip File
                        <input
                          type="file"
                          hidden
                          accept=".txt,.csv"
                          onChange={(e) => {
                            if (e.target.files && e.target.files[0]) {
                              const file = e.target.files[0];
                              const formData = new FormData();
                              formData.append('zipCodeFile', file);
                              
                              // Read the file content
                              const reader = new FileReader();
                              reader.onload = (event) => {
                                const content = event.target.result;
                                // Parse the content (assuming comma or newline separated)
                                const zipCodes = content
                                  .split(/[\n,]/)
                                  .map(zip => zip.trim())
                                  .filter(zip => zip !== '');
                                
                                // Update the form data
                                setFormData({
                                  ...formData,
                                  coveredZipCodes: zipCodes,
                                  zipCodeFile: file.name // Store the filename
                                });
                              };
                              reader.readAsText(file);
                            }
                          }}
                        />
                      </Button>
                    )}
                  </Box>
                  
                  {!isEditing && !isAdding && formData.coveredZipCodes.length > 0 && (
                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {formData.coveredZipCodes.map((zip, index) => (
                        <Chip key={index} label={zip} size="small" />
                      ))}
                    </Box>
                  )}
                  
                  {formData.zipCodeFile && (isEditing || isAdding) && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2">
                        Loaded from file: {formData.zipCodeFile}
                      </Typography>
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                    Contact Information
                  </Typography>
                  <Divider />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Contact Name"
                    name="contactName"
                    value={formData.contactName}
                    onChange={handleInputChange}
                    disabled={isReadOnly || (!isEditing && !isAdding)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Contact Phone"
                    name="contactPhone"
                    value={formData.contactPhone}
                    onChange={handleInputChange}
                    disabled={isReadOnly || (!isEditing && !isAdding)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Contact Email"
                    name="contactEmail"
                    value={formData.contactEmail}
                    onChange={handleInputChange}
                    disabled={isReadOnly || (!isEditing && !isAdding)}
                  />
                </Grid>
                {/* Replace Operational Hours TextField with ScheduleInput */}
                <Grid item xs={12}>
                   <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                     Operational Hours & Schedule
                   </Typography>
                   <Divider sx={{ mb: 2 }}/>
                   <ScheduleInput
                     // Pass flattened structure including open247
                     scheduleData={{
                       open247: formData.open247 || false,
                       ...(formData.schedule || {})
                     }}
                     onChange={(fieldPath, value) => {
                       console.log(`[FacilitiesTab.js] ScheduleInput onChange: fieldPath=${fieldPath}, value=`, value);
                       setFormData(prevFormData => {
                         let updatedFormData = { ...prevFormData };
                         
                         if (fieldPath === 'open247') {
                           // Handle open247 which is at the top level of formData
                           updatedFormData.open247 = value;
                         } else {
                           // Handle nested schedule fields
                           // Ensure schedule object exists
                           if (!updatedFormData.schedule) {
                             updatedFormData.schedule = {};
                           }
                           
                           // Use deepMerge to update the nested schedule object
                           // Construct a small object with the path and value to merge
                           let update = {};
                           let currentLevel = update;
                           const keys = fieldPath.split('.');
                           for (let i = 0; i < keys.length - 1; i++) {
                             currentLevel[keys[i]] = {};
                             currentLevel = currentLevel[keys[i]];
                           }
                           currentLevel[keys[keys.length - 1]] = value;
                           
                           updatedFormData.schedule = deepMerge(updatedFormData.schedule, update);
                         }
                         
                         console.log('[FacilitiesTab.js] Updated formData:', updatedFormData);
                         return updatedFormData;
                       });
                     }}
                     disabled={isReadOnly || (!isEditing && !isAdding)} // Disable if read-only OR not in edit/add mode
                   />
                 </Grid>
                 {/* Services Section */}
                 <Grid item xs={12}>
                   <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                     Services
                   </Typography>
                   <Divider />
                 </Grid>
                 <Grid item xs={12}>
                   
                   {/* Roadside Services */}
                   <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Roadside Services</Typography>
                   <FormGroup>
                     {/* Jump Starts */}
                     <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5, fontWeight: 'bold' }}>Jump Starts</Typography>
                     <Box sx={{ ml: 2 }}>
                       {['roadsideJumpStartsLightDuty', 'roadsideJumpStartsMediumDuty', 'roadsideJumpStartsHeavyDuty'].map((key) => (
                         <FormControlLabel
                           key={key}
                           control={
                             <Checkbox
                               checked={formData.services?.[key] || false}
                               onChange={(e) => {
                                 setFormData({
                                   ...formData,
                                   services: {
                                     ...formData.services,
                                     [key]: e.target.checked
                                   }
                                 });
                               }}
                               disabled={isReadOnly || (!isEditing && !isAdding)}
                             />
                           }
                           label={key.includes('Light') ? 'Light Duty' : key.includes('Medium') ? 'Medium Duty' : 'Heavy Duty'}
                         />
                       ))}
                     </Box>

                     {/* Tire Changes */}
                     <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5, fontWeight: 'bold' }}>Tire Changes</Typography>
                     <Box sx={{ ml: 2 }}>
                       {['roadsideTireChangesLightDuty', 'roadsideTireChangesMediumDuty', 'roadsideTireChangesHeavyDuty'].map((key) => (
                         <FormControlLabel
                           key={key}
                           control={
                             <Checkbox
                               checked={formData.services?.[key] || false}
                               onChange={(e) => {
                                 setFormData({
                                   ...formData,
                                   services: {
                                     ...formData.services,
                                     [key]: e.target.checked
                                   }
                                 });
                               }}
                               disabled={isReadOnly || (!isEditing && !isAdding)}
                             />
                           }
                           label={key.includes('Light') ? 'Light Duty' : key.includes('Medium') ? 'Medium Duty' : 'Heavy Duty'}
                         />
                       ))}
                     </Box>

                     {/* Lock Outs */}
                     <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5, fontWeight: 'bold' }}>Lock Outs</Typography>
                     <Box sx={{ ml: 2 }}>
                       {['roadsideLockOutsLightDuty', 'roadsideLockOutsMediumDuty', 'roadsideLockOutsHeavyDuty'].map((key) => (
                         <FormControlLabel
                           key={key}
                           control={
                             <Checkbox
                               checked={formData.services?.[key] || false}
                               onChange={(e) => {
                                 setFormData({
                                   ...formData,
                                   services: {
                                     ...formData.services,
                                     [key]: e.target.checked
                                   }
                                 });
                               }}
                               disabled={isReadOnly || (!isEditing && !isAdding)}
                             />
                           }
                           label={key.includes('Light') ? 'Light Duty' : key.includes('Medium') ? 'Medium Duty' : 'Heavy Duty'}
                         />
                       ))}
                     </Box>

                     {/* Fuel Deliveries */}
                     <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5, fontWeight: 'bold' }}>Fuel Deliveries</Typography>
                     <Box sx={{ ml: 2 }}>
                       {['roadsideFuelDeliveriesLightDuty', 'roadsideFuelDeliveriesMediumDuty', 'roadsideFuelDeliveriesHeavyDuty'].map((key) => (
                         <FormControlLabel
                           key={key}
                           control={
                             <Checkbox
                               checked={formData.services?.[key] || false}
                               onChange={(e) => {
                                 setFormData({
                                   ...formData,
                                   services: {
                                     ...formData.services,
                                     [key]: e.target.checked
                                   }
                                 });
                               }}
                               disabled={isReadOnly || (!isEditing && !isAdding)}
                             />
                           }
                           label={key.includes('Light') ? 'Light Duty' : key.includes('Medium') ? 'Medium Duty' : 'Heavy Duty'}
                         />
                       ))}
                     </Box>

                     {/* Winch Outs */}
                     <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5, fontWeight: 'bold' }}>Winch Outs</Typography>
                     <Box sx={{ ml: 2 }}>
                       {['winchOutsLightDuty', 'winchOutsMediumDuty', 'winchOutsHeavyDuty'].map((key) => (
                         <FormControlLabel
                           key={key}
                           control={
                             <Checkbox
                               checked={formData.services?.[key] || false}
                               onChange={(e) => {
                                 setFormData({
                                   ...formData,
                                   services: {
                                     ...formData.services,
                                     [key]: e.target.checked
                                   }
                                 });
                               }}
                               disabled={isReadOnly || (!isEditing && !isAdding)}
                             />
                           }
                           label={key.includes('Light') ? 'Light Duty' : key.includes('Medium') ? 'Medium Duty' : 'Heavy Duty'}
                         />
                       ))}
                     </Box>
                   </FormGroup>

                   {/* Towing Services */}
                   <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Towing Services</Typography>
                   <FormGroup>
                     <Box sx={{ ml: 2 }}>
                       {[
                         { key: 'lightDutyTowing', label: 'Light Duty Towing' },
                         { key: 'mediumDutyTowing', label: 'Medium Duty Towing' },
                         { key: 'heavyDutyTowing', label: 'Heavy Duty Towing' },
                         { key: 'accidentSceneTowing', label: 'Accident Scene Towing' },
                         { key: 'secondaryTow', label: 'Secondary Tow' }
                       ].map(({ key, label }) => (
                         <FormControlLabel
                           key={key}
                           control={
                             <Checkbox
                               checked={formData.services?.[key] || false}
                               onChange={(e) => {
                                 setFormData({
                                   ...formData,
                                   services: {
                                     ...formData.services,
                                     [key]: e.target.checked
                                   }
                                 });
                               }}
                               disabled={isReadOnly || (!isEditing && !isAdding)}
                             />
                           }
                           label={label}
                         />
                       ))}
                     </Box>
                   </FormGroup>

                   {/* Other Services */}
                   <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Other Services</Typography>
                   <FormGroup>
                     <Box sx={{ ml: 2 }}>
                       {[
                         { key: 'mobileMechanic', label: 'Mobile Mechanic' },
                         { key: 'mediumHeavyTire', label: 'Medium Heavy Tire' },
                         { key: 'storageFacility', label: 'Storage Facility' },
                         { key: 'keyMaking', label: 'Key Making' },
                         { key: 'locksmith', label: 'Locksmith' },
                         { key: 'electricCharging', label: 'Electric Charging' }
                       ].map(({ key, label }) => (
                         <FormControlLabel
                           key={key}
                           control={
                             <Checkbox
                               checked={formData.services?.[key] || false}
                               onChange={(e) => {
                                 setFormData({
                                   ...formData,
                                   services: {
                                     ...formData.services,
                                     [key]: e.target.checked
                                   }
                                 });
                               }}
                               disabled={isReadOnly || (!isEditing && !isAdding)}
                             />
                           }
                           label={label}
                         />
                       ))}
                     </Box>
                   </FormGroup>
                 </Grid>
               </Grid>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
                <Typography color="textSecondary">
                  Select a facility from the list or add a new one to view details.
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the facility "{facilityToDelete?.facilityName}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default FacilitiesTab;
